import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Award,
  Building2,
  ArrowLeft,
  DollarSign,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react'
import { useApi } from '../../shared/hooks/useApi'
import { decodeHtmlEntities } from '../../shared/utils/formatters'

interface ContractorOrganization {
  contractor_uuid: string
  contractor_name: string
  organization_name?: string
  organization_id?: string
  wins_count: number
  total_value?: number
  cpv_groups?: string[]
}

const ContractorDetailPage = () => {
  const { contractorUuid } = useParams<{ contractorUuid: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Get filters from URL parameters (year is optional, only filter if explicitly provided)
  const yearParam = searchParams.get('year')
  const year = yearParam ? parseInt(yearParam) : undefined
  const cpvFilter = searchParams.get('cpv') || ''
  const dateFrom = searchParams.get('date_from') || ''
  const dateTo = searchParams.get('date_to') || ''

  const { getContractorOrganizationsByUuid } = useApi()

  const [data, setData] = useState<ContractorOrganization[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sortConfig, setSortConfig] = useState<{
    key: keyof ContractorOrganization
    direction: 'asc' | 'desc'
  }>({
    key: 'wins_count',
    direction: 'desc',
  })

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        setError(null)

        const result = await getContractorOrganizationsByUuid(
          contractorUuid!,
          cpvFilter,
          dateFrom,
          dateTo,
          year,
        )

        setData(result)
      } catch (err) {
        console.error('Failed to load contractor organizations:', err)
        setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        setLoading(false)
      }
    }

    if (contractorUuid) {
      loadData()
    }
  }, [contractorUuid, cpvFilter, dateFrom, dateTo, year, getContractorOrganizationsByUuid])

  const sortedData = useMemo(() => {
    if (!data) return []

    return [...data].sort((a, b) => {
      // Handle null/undefined values
      const aValue = a[sortConfig.key] ?? 0
      const bValue = b[sortConfig.key] ?? 0

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1
      }
      return 0
    })
  }, [data, sortConfig])

  const requestSort = (key: keyof ContractorOrganization) => {
    let direction: 'asc' | 'desc' = 'asc'
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc'
    }
    setSortConfig({ key, direction })
  }

  const SortIcon = ({ columnKey }: { columnKey: keyof ContractorOrganization }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="h-4 w-4 ml-1 text-ink-faint" />
    return sortConfig.direction === 'asc' ? (
      <ArrowUp className="h-4 w-4 ml-1 text-brand" />
    ) : (
      <ArrowDown className="h-4 w-4 ml-1 text-brand" />
    )
  }

  const SortableHeader = ({
    label,
    columnKey,
    align = 'left',
    className = '',
  }: {
    label: string
    columnKey: keyof ContractorOrganization
    align?: 'left' | 'right'
    className?: string
  }) => (
    <th
      scope="col"
      className={`px-6 py-3 text-${align} text-xs font-medium text-ink-muted uppercase tracking-wider cursor-pointer hover:bg-surface-hover transition-colors ${className}`}
      onClick={() => requestSort(columnKey)}
    >
      <div className={`flex items-center ${align === 'right' ? 'justify-end' : ''}`}>
        {label}
        <SortIcon columnKey={columnKey} />
      </div>
    </th>
  )

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-danger/5 border border-danger/20 rounded-md p-4">
        <p className="text-danger">Błąd podczas ładowania danych organizacji: {error}</p>
      </div>
    )
  }

  const totalWins = data?.reduce((sum, item) => sum + item.wins_count, 0) || 0
  const totalValue = data?.reduce((sum, item) => sum + (item.total_value || 0), 0) || 0

  // Get contractor name from the first item (all items have the same contractor)
  const contractorName = data?.[0]?.contractor_name || contractorUuid || ''
  const decodedContractorName = decodeHtmlEntities(contractorName)

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="bg-surface-raised rounded-lg border border-border-subtle p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/contractors')}
              className="flex items-center space-x-2 text-ink-secondary hover:text-ink"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Powrót do wykonawców</span>
            </button>
            <div className="border-l border-border h-8"></div>
            <div>
              <h2 className="text-xl font-semibold text-ink flex items-center">
                <Award className="h-6 w-6 mr-2 text-success" />
                {decodedContractorName}
              </h2>
              <p className="text-sm text-ink-secondary mt-1">
                Zamawiający, dla których wykonawca wygrał postępowania
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-surface-raised rounded-lg border border-border-subtle p-6">
          <div className="flex items-center">
            <div className="p-2 bg-brand-bg rounded-lg">
              <Award className="h-6 w-6 text-brand" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ink-secondary">Razem zwycięstw</p>
              <p className="text-2xl font-semibold text-ink">
                {totalWins.toLocaleString('pl-PL')}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-surface-raised rounded-lg border border-border-subtle p-6">
          <div className="flex items-center">
            <div className="p-2 bg-success/10 rounded-lg">
              <DollarSign className="h-6 w-6 text-success" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ink-secondary">Wartość całkowita</p>
              <p className="text-xl font-semibold text-ink">
                {new Intl.NumberFormat('pl-PL', {
                  style: 'currency',
                  currency: 'PLN',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(totalValue)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-surface-raised rounded-lg border border-border-subtle p-6">
          <div className="flex items-center">
            <div className="p-2 bg-ai/10 rounded-lg">
              <Building2 className="h-6 w-6 text-ai" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ink-secondary">Zamawiających</p>
              <p className="text-2xl font-semibold text-ink">{data?.length || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Organizations Table */}
      <div className="bg-surface-raised rounded-lg border border-border-subtle overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="text-lg font-medium text-ink">
            Lista Zamawiających, u których wykonawca wygrał postępowania
          </h3>
          <p className="text-sm text-ink-secondary mt-1">
            Kliknij w nazwę Zamawiającego, aby zobaczyć wszystkie jego postępowania
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-surface">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">
                  #
                </th>
                <SortableHeader label="Zamawiający" columnKey="organization_name" />
                <SortableHeader label="Wartość całkowita" columnKey="total_value" align="right" />
                <SortableHeader label="Wygrane postępowania" columnKey="wins_count" align="right" />
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">
                  Kategorie CPV
                </th>
              </tr>
            </thead>
            <tbody className="bg-surface-raised divide-y divide-border">
              {sortedData.map((item, index) => (
                <tr key={index} className="hover:bg-surface-hover">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-muted">{index + 1}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => {
                        const orgName = decodeHtmlEntities(item.organization_name?.trim() || '')
                        navigate(
                          `/contractors/relationship/${encodeURIComponent(orgName)}/${contractorUuid}?contractorName=${encodeURIComponent(decodedContractorName)}`,
                        )
                      }}
                      className="text-sm font-medium text-brand hover:text-brand hover:underline"
                    >
                      {decodeHtmlEntities(item.organization_name || '')}
                    </button>
                    {item.organization_id && (
                      <div className="text-xs text-ink-muted mt-1">ID: {item.organization_id}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-ink text-right">
                    {item.total_value
                      ? new Intl.NumberFormat('pl-PL', {
                          style: 'currency',
                          currency: 'PLN',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        }).format(item.total_value)
                      : 'Brak danych'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-ink text-right">
                    {item.wins_count.toLocaleString('pl-PL')}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {item.cpv_groups?.slice(0, 3).map((cpv, cpvIndex) => (
                        <button
                          key={cpvIndex}
                          onClick={() => {
                            const params = new URLSearchParams(searchParams)
                            params.set('cpv', cpv)
                            navigate(`/contractors?${params.toString()}`)
                          }}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-ai/10 text-ai hover:bg-ai/10"
                        >
                          {cpv}
                        </button>
                      ))}
                      {item.cpv_groups && item.cpv_groups.length > 3 && (
                        <span className="text-xs text-ink-muted">
                          +{item.cpv_groups.length - 3} więcej
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {(!data || data.length === 0) && (
          <div className="px-6 py-8 text-center text-ink-muted">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-ink-faint" />
            <p>Brak zamawiających dla tego wykonawcy w wybranym okresie</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default ContractorDetailPage
